<script lang="ts">
    import {AlertCircle, Clock} from '@lucide/svelte';
    import {EXP_RANGE_OPTIONS, FREQUENCY_OPTIONS} from '../../form/config';
    import type {
        ExperienceRangeOption,
        FormData,
        FrequencyOption,
        ToggleList,
        UpdateField,
    } from '../../form/types';
    import Button from "../common/Button.svelte";
    import Input from "../common/Input.svelte";
    import {fly, slide} from "svelte/transition";
    import {FormAttributesStore} from "../../form/attributes";

    let {
        valid = $bindable(false),
        formData,
        updateField,
        toggleList,
    }: {
        valid?: boolean;
        formData: FormData;
        updateField: UpdateField;
        toggleList: ToggleList;
    } = $props();

    const readInputValue = (event: Event): string =>
        (event.currentTarget as HTMLInputElement | HTMLSelectElement).value;

    const readChecked = (event: Event): boolean =>
        (event.currentTarget as HTMLInputElement).checked;

    const isFrequencyOption = (value: string): value is FrequencyOption =>
        (FREQUENCY_OPTIONS as readonly string[]).includes(value);

    const isExperienceRangeOption = (value: string): value is ExperienceRangeOption =>
        (EXP_RANGE_OPTIONS as readonly string[]).includes(value);

    const updateMethodFrequency = (method: string, rawValue: string): void => {
        const next = {...formData.methodologyFrequencies};

        if (isFrequencyOption(rawValue)) {
            next[method] = rawValue;
        } else {
            delete next[method];
        }

        updateField('methodologyFrequencies', next);
    };

    const updateMethodExpRange = (method: string, rawValue: string): void => {
        const next = {...formData.methodologyExpRange};

        if (isExperienceRangeOption(rawValue)) {
            next[method] = rawValue;
        } else {
            delete next[method];
        }

        updateField('methodologyExpRange', next);
    };

    let methods = $derived.by(() => {
        return formData.methodologies.filter(m => m !== 'Anders...');
    })


    $effect(() => {
        if (formData.openForBridgeCare === null) {
            valid = false;
            return;
        }

        if (formData.openForBridgeCare === true && !formData.baAwareness) {
            valid = false;
            return;
        }


        valid = methods.every((m) => {
            const is_anders = m === 'Anders...';
            const has_Education = formData.methodologyEducation[m] !== undefined;
            const has_Expkrange = formData.methodologyExpRange[m] !== undefined;
            const has_frequencies = formData.methodologyFrequencies[m] !== undefined;
            return (has_Education && has_Expkrange && has_frequencies) || is_anders;
        });
    });
</script>

<div class="space-y-8 animate-in fade-in duration-500">
    <div class="space-y-4">
        <h2 class="flex items-center gap-2 text-xl font-bold text-slate-800">
            <AlertCircle class="text-amber-600" size={24}/>
            Overbrugging & Capaciteit
        </h2>
        <div class="space-y-3 rounded-[2rem] border border-amber-100 bg-amber-50 p-5 shadow-sm">
            <p class="text-[11px] font-medium leading-relaxed text-amber-800">
                Sta je open om overbruggingsdbegeleiding te verzorgen aan werknemers die op een wachtlijst staan voor
                Specialistische GGZ?
                <br> <br>
                Let op: bij overbruggingsbegeleiding bied je geen therapeutische interventies, maar richt je je op
                praktische support en psychische stabilisatie tijdens de wachtperiode.
            </p>
            <div class="flex gap-4 pt-1">
                <button
                        type="button"
                        onclick={() => updateField('openForBridgeCare', true)}
                        class={`rounded-xl px-6 py-2.5 text-xs font-bold transition-all ${formData.openForBridgeCare ===
          true
            ? 'scale-105 bg-amber-600 text-white shadow-lg'
            : 'border border-amber-200 bg-white text-amber-700'}`}
                >
                    Ja
                </button>
                <button
                        type="button"
                        onclick={() => updateField('openForBridgeCare', false)}
                        class={`rounded-xl px-6 py-2.5 text-xs font-bold transition-all ${formData.openForBridgeCare ===
          false
            ? 'bg-slate-300 text-slate-700 shadow-md'
            : 'border border-slate-200 bg-white text-slate-500'}`}
                >
                    Nee
                </button>
            </div>
            {#if formData.openForBridgeCare === true}
                <label
                        transition:slide|local={{ axis: "y", duration: 300 }}
                        class="animate-in  mt-2 flex cursor-pointer items-center gap-3 rounded-2xl border border-amber-200 bg-white/60 p-3"
                >
                    <input
                            type="checkbox"
                            class="mt-1 h-4 w-4 rounded text-amber-600"
                            checked={formData.baAwareness}
                            onchange={(event) => updateField('baAwareness', readChecked(event))}
                    />
                    <span class="text-[10px] italic font-medium leading-snug text-amber-900">
                        Ik ben me ervan bewust dat dit intensievere afstemming met de bedrijfsarts en/of huisarts vraagt.
                    </span>
                </label>
            {/if}
        </div>
    </div>

    <div class="space-y-4">
        <h2 class="flex items-center gap-3 text-xl font-bold text-slate-800">
            <Clock class="text-blue-600" size={24}/>
            Methodieken
        </h2>
        <p
                class="text-xs text-slate-900"
        >
            Welke methodieken zet je als psycholoog actief en met regelmaat in? Geef per methodiek aan hoe vaak je het
            inzet, welke opleiding / scholing je hiervoor gedaan hebt, en hoeveel jaar ervaring je ermee hebt.

        </p>

        <div class="grid grid-cols-2 gap-2 md:grid-cols-3">
            {#each $FormAttributesStore.data.psych_methods as method}
                <Button
                        onclick={() => toggleList('methodologies', method)}
                        active={formData.methodologies.includes(method)}
                >
                    {method}
                </Button>
            {/each}
        </div>

        {#if formData.methodologies.includes('Anders...')}
            <div
                    in:fly={{ y: 20, duration: 300 }}
                    out:fly={{ y: 20, duration: 300 }}
            >
                <Input
                        type="text"
                        placeholder="Vul hier de belangrijkste methodiek in..."
                        bind:value={formData.otherMethodologyText}
                />
            </div>
        {/if}

        {#if methods.length > 0}
            <div
                    in:fly={{ y: 20, duration: 300 }}
                    out:fly={{ y: 20, duration: 300 }}
                    class="mt-6 space-y-4 rounded-3xl border border-slate-100 bg-slate-50 p-4 shadow-inner">
                <p class="ml-1 text-[10px] font-bold uppercase tracking-widest text-slate-600">
                    Details per methodiek:
                </p>

                <div class="custom-scrollbar space-y-3 overflow-y-auto pr-2">
                    {#each methods as method}
                        <div
                                in:fly={{y:-20, duration: 300}}
                                out:fly={{y:20, duration: 300}}
                                class="space-y-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <span class="text-[11px] font-bold text-slate-800"
              >{method === 'Anders...' ? formData.otherMethodologyText || 'Anders' : method}</span
              >
                            <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
                                <select
                                        class="rounded-xl border bg-slate-50 p-2 text-[10px] outline-none"
                                        onchange={(event) => updateMethodFrequency(method, readInputValue(event))}
                                >
                                    <option value="" selected={!formData.methodologyFrequencies[method]}>Frequentie...
                                    </option>
                                    {#each FREQUENCY_OPTIONS as frequency}
                                        <option
                                                value={frequency}
                                                selected={formData.methodologyFrequencies[method] === frequency}>{frequency}</option
                                        >
                                    {/each}
                                </select>
                                <input
                                        type="text"
                                        placeholder="Welke opleiding?"
                                        class="rounded-xl border bg-slate-50 p-2 text-[10px] outline-none focus:ring-1 focus:ring-blue-400"
                                        value={formData.methodologyEducation[method] ?? ''}
                                        oninput={(event) =>
                    updateField('methodologyEducation', {
                      ...formData.methodologyEducation,
                      [method]: readInputValue(event),
                    })}
                                />

                                <select
                                        class="rounded-xl border bg-slate-50 p-2 text-[10px] outline-none"
                                        onchange={(event) => updateMethodExpRange(method, readInputValue(event))}
                                >
                                    <option value="" selected={!formData.methodologyExpRange[method]}>Ervaring...
                                    </option>
                                    {#each EXP_RANGE_OPTIONS as range}
                                        <option value={range} selected={formData.methodologyExpRange[method] === range}
                                        >{range}</option
                                        >
                                    {/each}
                                </select>
                            </div>
                        </div>
                    {/each}
                </div>
            </div>
        {/if}
    </div>
</div>
<style>
    @import "tailwindcss";
</style>
